import type { ReactNode } from "react";

import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";

/*
 * Day grouping shared by the workspace Feed page and the channel feed tab so
 * both surfaces cut the stream at the same boundaries with the same labels.
 */

export type FeedDayGroup<T> = { id: string; label: string; items: T[] };

export function feedDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export function feedDayLabel(date: Date, today: Date): string {
  if (feedDayKey(date) === feedDayKey(today)) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (feedDayKey(date) === feedDayKey(yesterday)) return "Yesterday";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  }).format(date);
}

export function groupFeedByDay<T>(
  items: T[],
  occurredAt: (item: T) => number,
  now = Date.now(),
): FeedDayGroup<T>[] {
  const groups = new Map<string, FeedDayGroup<T>>();
  const today = new Date(now);
  for (const item of items) {
    const date = new Date(occurredAt(item));
    const id = feedDayKey(date);
    const existing = groups.get(id);
    if (existing) existing.items.push(item);
    else
      groups.set(id, { id, label: feedDayLabel(date, today), items: [item] });
  }
  return [...groups.values()];
}

/** The small caps label that names a section of a feed or a thread. */
export function feedSectionLabelClass(tone: "muted" | "primary" = "muted") {
  return cn(
    "shrink-0 text-xs font-medium uppercase tracking-wide",
    tone === "primary" ? "text-primary" : "text-muted-foreground",
  );
}

export function FeedSectionLabel({
  children,
  tone = "muted",
  className,
}: {
  children: ReactNode;
  tone?: "muted" | "primary";
  className?: string;
}) {
  return (
    <span className={cn(feedSectionLabelClass(tone), className)}>
      {children}
    </span>
  );
}

export function FeedDayHeader({ id, label }: { id?: string; label: string }) {
  return (
    <div className="flex items-center gap-3 pt-4 pb-1">
      <h3 id={id} className={feedSectionLabelClass()}>
        {label}
      </h3>
      <span
        aria-hidden
        className="h-px min-w-4 flex-1 border-t border-dashed border-border/70"
      />
    </div>
  );
}

export function FeedEmpty({ children }: { children: ReactNode }) {
  return <EmptyState>{children}</EmptyState>;
}

const SKELETON_WIDTHS = ["w-3/4", "w-2/3", "w-3/5", "w-1/2"] as const;

export function FeedSkeleton({
  rows = SKELETON_WIDTHS.length,
  header = true,
  label = "Loading feed",
}: {
  rows?: number;
  header?: boolean;
  label?: string;
}) {
  return (
    <div
      aria-label={label}
      role="status"
      className="animate-pulse py-3 motion-reduce:animate-none"
    >
      {header ? (
        <div className="mb-3 flex items-center gap-2">
          <span className="h-2.5 w-12 rounded-sm bg-muted" />
          <span className="h-px flex-1 bg-border/60" />
        </div>
      ) : null}
      <div className="space-y-3">
        {SKELETON_WIDTHS.slice(0, rows).map((width) => (
          <div key={width} className="flex items-start gap-3 px-2 py-2">
            <span className="size-8 shrink-0 rounded-full bg-muted" />
            <span className="min-w-0 flex-1 space-y-1.5">
              <span className={cn("block h-3.5 rounded-sm bg-muted", width)} />
              <span className="block h-2.5 w-40 rounded-sm bg-muted/75" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
